import { memo, useCallback, useEffect, useState } from 'react'

import { IContact } from 'clickNgo/entities/contact'
import { useGetContactsByPartnerQuery } from 'clickNgo/entities/contact/api'
import { fetchPartnerRequestById } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { ContactCreator } from 'clickNgo/features/contact/contactCreator'
import { useBackup, useContactData } from 'clickNgo/features/contact/contactDeactivation/contexts'
import { Nullable } from 'clickNgo/models/utils'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { NotificationController } from 'core-components/library/popup'

import { ContactRelinkingStep } from '../../../configs'
import { useReassignment } from '../../../contexts/ReassignmentContext'
import { ContactRelinkingStepProps } from '../../../models'
import { contactRelinkingModalTranslations } from '../../../translation'
import { ContactRelinkingFooter } from '../../contactRelinkingFooter'
import { ContactRelinkingHeader } from '../../contactRelinkingHeader'
import { SkipStepWarning } from '../../skipStepWarning/SkipStepWarning'

import styles from './BackupContactStep.module.scss'

export const BackupContactStep = memo<ContactRelinkingStepProps>(({ onCancel, onProceed }) => {
  const [isCreateContactVisible, setIsCreateContactVisible] = useState(false)
  const [isLoadingPartner, setIsLoadingPartner] = useState(false)
  const {
    rolesToReassign,
    flagsToReassign,
    uniqueRolesToReassign,
    setAvailableContacts,
    availableContacts,
  } = useReassignment()
  const { contact } = useContactData()
  const { backupedContact, setBackupedContact } = useBackup()
  const [partner, setPartner] = useState<Nullable<IPartner>>(null)

  const [isSkipConfirmationVisible, setIsSkipConfirmationVisible] = useState(false)

  const { refetch: refetchContacts } = useGetContactsByPartnerQuery(
    {
      topPartnerId: contact?.topPartnerId,
    },
    {
      skip: !contact?.topPartnerId,
    }
  )

  const handleProceedClick = useCallback(() => {
    if (
      backupedContact &&
      !availableContacts.find((el) => el.contactId === backupedContact.contactId)
    ) {
      setAvailableContacts([...availableContacts, backupedContact])
    }

    if (availableContacts.length > 0 && uniqueRolesToReassign.length > 0) {
      onProceed(ContactRelinkingStep.ReassignUniqueRoles)
      return
    }

    if (availableContacts.length > 0 && rolesToReassign.length > 0) {
      onProceed(ContactRelinkingStep.ReassignRoles)
      return
    }

    if (availableContacts.length > 0 && flagsToReassign.length > 0) {
      onProceed(ContactRelinkingStep.ReassignFlags)
      return
    }

    onProceed(ContactRelinkingStep.Completion)
  }, [
    availableContacts,
    backupedContact,
    flagsToReassign.length,
    onProceed,
    rolesToReassign.length,
    setAvailableContacts,
    uniqueRolesToReassign.length,
  ])

  const handleCreateContactClick = useCallback(() => {
    setIsCreateContactVisible(true)
  }, [])

  const handleContactCreatorClose = useCallback(() => {
    setIsCreateContactVisible(false)
  }, [])

  const handleContactCreatorCreate = useCallback(
    (newContact: IContact) => {
      setIsCreateContactVisible(false)
      setBackupedContact(newContact)
      refetchContacts()
    },
    [refetchContacts, setBackupedContact]
  )

  const handleSkipConfirmationClose = useCallback(() => {
    setIsSkipConfirmationVisible(false)
  }, [])

  const onContinue = useCallback(() => {
    if (backupedContact) {
      handleProceedClick()
    } else {
      setIsSkipConfirmationVisible(true)
    }
  }, [backupedContact, handleProceedClick])

  useEffect(() => {
    if (contact?.partnerId) {
      setIsLoadingPartner(true)
      fetchPartnerRequestById(contact.partnerId, { cacheKey: contact.partnerId })
        .then((result) => {
          setPartner(result)
        })
        .catch(() => {
          setPartner(null)
          NotificationController.showError({
            message: 'Failed to fetch partner data.',
          })
        })
        .finally(() => {
          setIsLoadingPartner(false)
        })
    }
  }, [contact.partnerId])

  return (
    <div className={styles.container}>
      <ContactRelinkingHeader
        title={contactRelinkingModalTranslations.backupStep.title}
        description={contactRelinkingModalTranslations.backupStep.description}
        descriptionParams={[{ key: '{email}', value: contact.email }]}
      />
      <div className={styles.content}>
        <LoaderOverlay isVisible={isLoadingPartner} message="Loading partner data..." />
        <RenderWhen condition={backupedContact === null}>
          <div className={styles.action}>
            <p>
              <Translation text={contactRelinkingModalTranslations.backupStep.content} span />
            </p>
            <div>
              <Button color="success" onClick={handleCreateContactClick}>
                <Translation
                  text={contactRelinkingModalTranslations.backupStep.buttons.create}
                  span
                />
              </Button>
            </div>
          </div>
        </RenderWhen>
        <RenderWhen condition={backupedContact !== null}>
          <div className={styles.data}>
            <p className={styles.label}>
              <Translation text={contactRelinkingModalTranslations.backupStep.newContact} span />
            </p>
            <div className={styles.value}>
              {`${backupedContact?.firstName} ${backupedContact?.lastName}`}
            </div>
            <div className={styles.value}>{backupedContact?.email}</div>
          </div>
        </RenderWhen>
      </div>
      <ContactRelinkingFooter onCancel={onCancel} onProceed={onContinue} />
      <ContactCreator
        isVisible={isCreateContactVisible}
        onClose={handleContactCreatorClose}
        onCreate={handleContactCreatorCreate}
        partner={partner!}
        onReactivate={handleContactCreatorCreate}
      />
      <SkipStepWarning
        isVisible={isSkipConfirmationVisible}
        handleSkipConfirmationClose={handleSkipConfirmationClose}
        handleSkipConfirmationProceed={handleProceedClick}
        skipTranslations={{
          title: contactRelinkingModalTranslations.backupSkipConfirm.title,
          description: contactRelinkingModalTranslations.backupSkipConfirm.description,
        }}
      />
    </div>
  )
})

BackupContactStep.displayName = 'ContactRelinkingBackupContactStep'
